import { useMutation, useQueryClient } from "@tanstack/react-query";

import { addTeamMember } from "api/landlord/landlord";
import { FC } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Modal
} from "shared/components";
import { getErrorMessage } from "utils/error";
import { useForm } from "shared/hooks";
import { object, string } from "yup";
import { toast } from "react-toastify";
import { XClose } from "untitledui-js-base";
import { RoleSelect } from "../../settings-users-id/components/RoleSelect";
import { ROLE } from "providers/Auth";
import { useNavigate } from "react-router-dom";

type Props = {
  opened: boolean;
  onClose: () => void;
  queryKey: unknown[];
};

const invite = object({
  name: string().required("Name is required"),
  email: string()
    .test("pattern", "Email is not valid", (value) => {
      if (!value) return true;
      return Boolean(
        value &&
          /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,12}$/.exec(value)
      );
    })
    .required("Email is required")
});

export const InviteNewMemberModal: FC<Props> = ({
  opened,
  onClose,
  queryKey
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, onSubmit, values, reset, handleChange } = useForm(
    {
      name: "",
      email: "",
      role: ROLE.PropertyManager
    },
    invite
  );
  const { mutate } = useMutation({
    mutationFn: (data: { name: string; email: string; role: ROLE }) => {
      return addTeamMember(data);
    },
    onSuccess: (response) => {
      navigate(`/settings/users/${response?.id}`);
      toast.success(`${values.name} was invited`);
      queryClient.invalidateQueries({ queryKey });
      reset();
      onClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      panelClassName="max-w-[658px] w-full p-6 border border-alpha-100 shadow-lg"
    >
      <div className="flex mb-4 items-center justify-between">
        <header className="text-xl font-bold text-content-1000">
          Invite a new member
        </header>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.BASE}
          borderLess
          className="translate-x-2"
          onClick={onClose}
          Icon={XClose}
        />
      </div>
      <form
        onSubmit={onSubmit({
          onSuccess: () =>
            mutate({
              name: values.name,
              email: values.email,
              role: values.role
            })
        })}
      >
        <div className="mb-4">
          <Input
            id="full_name"
            label="Full Name"
            placeholder="Jakob Dias"
            required={true}
            {...register("name")}
          />
        </div>
        <div className="flex gap-4">
          <div className="w-full">
            <Input
              id="email"
              label="Email"
              placeholder="example@gmail.com"
              required={true}
              {...register("email")}
            />
          </div>
          <div className="w-full">
            <RoleSelect
              role={values.role}
              onChange={(e) =>
                handleChange("role", () => e.target.value || "")({})
              }
            />
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <Button
            size={ButtonSize.BASE}
            onClick={() => {
              reset();
              onClose();
            }}
            className="w-[125px]"
          >
            Cancel
          </Button>

          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            type="submit"
            className="w-[125px]"
          >
            Invite
          </Button>
        </div>
      </form>
    </Modal>
  );
};
